import { useRef, useState } from 'react';
import { getUnionStackClient } from '../unionStackClient';

/* ---------------------------------------------------------------------------
 * PREVIOUS FILESTACK IMPLEMENTATION — kept for reference, replaced by UnionStack.
 *
 * Commented out rather than deleted: `filestack.init()` ran at module load, so
 * leaving it active would fire on every page importing this hook and the two
 * clients would both try to own the upload. The `filestack-js` dependency is
 * still installed, so this block can be restored verbatim if needed.
 *
 * import * as filestack from "filestack-js";
 *
 * const filestackClient = filestack.init("AASYllbvHQ3u0A1GxbOvZz");
 * const filestackToken = {};
 *
 *   const uploadFile = async (files, fileFor) => {
 *     try {
 *       console.log("in uploadFile")
 *       resetState();
 *       setIsFileUploading(true);
 *
 *       const uploadPromises = Object.keys(files).map(async (key) => {
 *         let filestackUploadResponse ;
 *         try {
 *            filestackUploadResponse =  await filestackClient.upload(files[key], {}, filestackToken)
 *         } catch(err) {
 *           throw new Error("Error uploading file");
 *         }
 *
 *         return {
 *           name: files[key].name,
 *           url: `https://filestack-mastersunion.s3.ap-south-1.amazonaws.com/${filestackUploadResponse.key}`,
 *           size: bytesToSize(filestackUploadResponse?._file?.size),
 *           fileFor
 *         };
 *       });
 *
 *       console.log("uploadPromises", uploadPromises)
 *       const uploadedFiles = await Promise.all(uploadPromises);
 *       setUploadedFileData(uploadedFiles);
 *       setFileUploadSuccess(true);
 *     } catch (err) {
 *       setFileUploadError(err.message);
 *     } finally {
 *       setIsFileUploading(false);
 *     }
 *   };
 * ------------------------------------------------------------------------- */

const useMultiUpload = () => {
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [fileUploadSuccess, setFileUploadSuccess] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  const [uploadedFileData, setUploadedFileData] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const abortRef = useRef(null);

  const resetState = () => {
    setIsFileUploading(false);
    setFileUploadSuccess(false);
    setFileUploadError(null);
    setUploadedFileData([]);
    setUploadProgress(0);
  };

  const cancelUpload = () => {
    abortRef.current?.abort();
  };

  const uploadFile = async (files, fileFor) => {
    try {
      resetState();
      setIsFileUploading(true);

      // Accepts a FileList or a plain array of Files.
      const fileList = Array.from(files ?? []);
      if (fileList.length === 0) {
        throw new Error('No files selected for upload');
      }

      const controller = new AbortController();
      abortRef.current = controller;

      const totalBytes = fileList.reduce((sum, f) => sum + (f.size || 0), 0);
      const loadedBytes = new Array(fileList.length).fill(0);

      // Uploads run in parallel, but results keep input order — consumers
      // align uploadedFileData with their own selection list by index.
      const results = await Promise.all(
        fileList.map(async (file, index) => {
          try {
            const uploaded = await getUnionStackClient().upload(file, {
              signal: controller.signal,
              metadata: fileFor !== undefined ? { fileFor } : undefined,
              onFileUploadProgress: (pickedFile, ev) => {
                loadedBytes[index] = ev.loaded;
                if (totalBytes > 0) {
                  const loaded = loadedBytes.reduce((a, b) => a + b, 0);
                  setUploadProgress(Math.min(100, Math.round((loaded / totalBytes) * 100)));
                }
              },
            });
            return { file, uploaded };
          } catch (error) {
            console.error(`Failed to upload ${file.name}:`, error);
            return { file, error };
          }
        })
      );

      const uploadedFiles = results
        .filter((result) => result.uploaded)
        .map(({ file, uploaded }) => ({
          name: file.name,
          url: uploaded.url,
          size: bytesToSize(uploaded.size ?? file.size),
          fileFor,
        }));

      // Partial failures are tolerated (matching the previous behavior):
      // the error state is only set when every file in the batch failed.
      if (uploadedFiles.length === 0) {
        const failed = results.find((result) => result.error);
        const error = failed?.error;
        // UnionStack rejects with an UploadError ({ code, message }) rather than
        // a plain Error, so `message` alone loses the reason (VALIDATION vs
        // QUOTA vs AUTH). Surface the code and filename — this string is what
        // the consuming page shows in its toast.
        const reason = error?.message || 'Upload failed';
        const code = error?.code ? ` [${error.code}]` : '';
        const which = failed?.file?.name ? `${failed.file.name}: ` : '';
        throw new Error(`${which}${reason}${code}`);
      }

      setUploadedFileData(uploadedFiles);
      setFileUploadSuccess(true);
    } catch (err) {
      console.error('Upload process failed:', err);
      setFileUploadError(err.message);
      setUploadedFileData([]);
      setFileUploadSuccess(false);
    } finally {
      abortRef.current = null;
      setIsFileUploading(false);
    }
  };

  const bytesToSize = (bytes) => {
    var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "n/a";
    var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    if (i === 0) return bytes + " " + sizes[i];
    return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
  };

  return {
    isFileUploading,
    fileUploadError,
    fileUploadSuccess,
    uploadedFileData,
    uploadFile,
    resetState,
    uploadProgress,
    cancelUpload,
  };
};

export default useMultiUpload;
